import { useEffect, useRef, useState } from "react";

import * as api from "../services/groupMemberVideosService";

const LIMIT = 12;

const getMessage = (error) =>
  error?.response?.data?.message || error?.message || "Request failed.";

const mergeVideos = (current, incoming) => {
  const videos = new Map();

  [...current, ...incoming].forEach((video) => {
    const key = video?.videoId ?? video?.videoTag;

    if (key != null) {
      videos.set(String(key), video);
    }
  });

  return Array.from(videos.values());
};

export const useGroupMemberVideos = (groupTag) => {
  const [videoThumbs, setVideoThumbs] = useState([]);
  const [video, setVideo] = useState(null);

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [playing, setPlaying] = useState(false);

  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState("");
  const [watchError, setWatchError] = useState("");

  const generationRef = useRef(0);
  const playbackRequestRef = useRef(0);

  const listControllerRef = useRef(null);
  const moreControllerRef = useRef(null);
  const playbackControllerRef = useRef(null);

  const listLoadingRef = useRef(false);
  const moreLoadingRef = useRef(false);

  const hasMoreRef = useRef(false);
  const nextOffsetRef = useRef(0);

  const getMemberGroupVideoThumbs = async (limit = LIMIT, offset = 0) => {
    if (!groupTag) return false;

    const pageLimit = Number(limit);
    const pageOffset = Number(offset);

    if (
      !Number.isSafeInteger(pageLimit) ||
      pageLimit < 1 ||
      pageLimit > 50 ||
      !Number.isSafeInteger(pageOffset) ||
      pageOffset < 0
    ) {
      setError("Invalid video pagination.");
      return false;
    }

    const isLoadMore = pageOffset > 0;

    if (
      isLoadMore &&
      (listLoadingRef.current || moreLoadingRef.current || !hasMoreRef.current)
    ) {
      return false;
    }

    const generation = generationRef.current;
    const controller = new AbortController();

    if (isLoadMore) {
      moreControllerRef.current = controller;
      moreLoadingRef.current = true;
      setLoadingMore(true);
    } else {
      listControllerRef.current?.abort();
      moreControllerRef.current?.abort();

      listControllerRef.current = controller;

      listLoadingRef.current = true;
      moreLoadingRef.current = false;

      nextOffsetRef.current = 0;
      hasMoreRef.current = false;

      setVideoThumbs([]);
      setHasMore(false);
      setLoading(true);
      setLoadingMore(false);
    }

    setError("");

    try {
      const response = await api.getMemberGroupVideoThumbs(
        groupTag,
        pageLimit,
        pageOffset,
        controller.signal,
      );

      if (controller.signal.aborted || generation !== generationRef.current) {
        return false;
      }

      if (!Array.isArray(response?.data)) {
        throw new Error("Invalid group videos response.");
      }

      const items = response.data;
      const moreAvailable = items.length === pageLimit;

      setVideoThumbs((current) =>
        isLoadMore ? mergeVideos(current, items) : items,
      );

      nextOffsetRef.current = pageOffset + items.length;
      hasMoreRef.current = moreAvailable;

      setHasMore(moreAvailable);

      return items;
    } catch (requestError) {
      if (!controller.signal.aborted && generation === generationRef.current) {
        setError(getMessage(requestError));
      }

      return false;
    } finally {
      if (generation === generationRef.current) {
        if (isLoadMore) {
          if (moreControllerRef.current === controller) {
            moreLoadingRef.current = false;
            moreControllerRef.current = null;
            setLoadingMore(false);
          }
        } else if (listControllerRef.current === controller) {
          listLoadingRef.current = false;
          listControllerRef.current = null;
          setLoading(false);
        }
      }
    }
  };

  const loadMoreMemberGroupVideos = async () => {
    if (!hasMoreRef.current) return false;

    return getMemberGroupVideoThumbs(LIMIT, nextOffsetRef.current);
  };

  const getMemberGroupVideoPlay = async (videoTag) => {
    const tag = String(videoTag || "").trim();

    if (!groupTag || !tag) {
      setWatchError("Group and video are required.");
      return false;
    }

    const generation = generationRef.current;
    const requestId = ++playbackRequestRef.current;

    playbackControllerRef.current?.abort();

    const controller = new AbortController();
    playbackControllerRef.current = controller;

    setVideo(null);
    setPlaying(true);
    setWatchError("");

    try {
      const response = await api.getMemberGroupVideoPlay(
        groupTag,
        tag,
        controller.signal,
      );

      if (
        controller.signal.aborted ||
        generation !== generationRef.current ||
        requestId !== playbackRequestRef.current
      ) {
        return false;
      }

      const data = response?.data;

      if (
        typeof data?.videoUrl !== "string" &&
        typeof data?.playbackUrl !== "string"
      ) {
        throw new Error("Video playback URL is unavailable.");
      }

      setVideo(data);

      return data;
    } catch (requestError) {
      if (
        !controller.signal.aborted &&
        generation === generationRef.current &&
        requestId === playbackRequestRef.current
      ) {
        setVideo(null);
        setWatchError(getMessage(requestError));
      }

      return false;
    } finally {
      if (
        generation === generationRef.current &&
        requestId === playbackRequestRef.current
      ) {
        setPlaying(false);
        playbackControllerRef.current = null;
      }
    }
  };

  const clearVideoPlayback = () => {
    playbackRequestRef.current += 1;

    playbackControllerRef.current?.abort();
    playbackControllerRef.current = null;

    setVideo(null);
    setPlaying(false);
    setWatchError("");
  };

  useEffect(() => {
    generationRef.current += 1;
    playbackRequestRef.current += 1;

    listControllerRef.current?.abort();
    moreControllerRef.current?.abort();
    playbackControllerRef.current?.abort();

    listLoadingRef.current = false;
    moreLoadingRef.current = false;
    hasMoreRef.current = false;
    nextOffsetRef.current = 0;

    setVideoThumbs([]);
    setVideo(null);

    setLoading(false);
    setLoadingMore(false);
    setPlaying(false);

    setHasMore(false);
    setError("");
    setWatchError("");

    if (groupTag) {
      void getMemberGroupVideoThumbs(LIMIT, 0);
    }

    return () => {
      generationRef.current += 1;
      playbackRequestRef.current += 1;

      listControllerRef.current?.abort();
      moreControllerRef.current?.abort();
      playbackControllerRef.current?.abort();
    };
  }, [groupTag]);

  return {
    videoThumbs,
    video,

    loading,
    loadingMore,
    playing,

    hasMore,
    error,
    watchError,

    setVideo,
    setError,

    getMemberGroupVideoThumbs,
    loadMoreMemberGroupVideos,
    getMemberGroupVideoPlay,
    clearVideoPlayback,
  };
};

export default useGroupMemberVideos;
